import { useState } from "react";
import { ArrowRight, Sparkle, ChartLineUp } from "@phosphor-icons/react";
import api, { formatApiError, errContext } from "../lib/api";
import ShareButtons from "../components/ShareButtons";
import CashForecastTeaser from "../components/CashForecastTeaser";

const fmt$ = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const DEFAULTS = {
  email: "", company: "", monthly_revenue: 50000, monthly_expenses: 35000,
  cash_on_hand: 120000, accounts_receivable: 25000, accounts_payable: 12000, headcount: 10,
};

export default function CashHealthCheckPage() {
  const [form, setForm] = useState(DEFAULTS);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true); setResult(null);
    try {
      const payload = { ...form };
      ["monthly_revenue", "monthly_expenses", "cash_on_hand", "accounts_receivable", "accounts_payable", "headcount"].forEach((k) => { payload[k] = Number(form[k]); });
      const { data } = await api.post("/public/cash-health-check", payload);
      setResult(data);
      // Scroll to results
      setTimeout(() => document.getElementById("report-section")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, errContext(err)) || err.message);
    } finally { setLoading(false); }
  };

  const scoreColor = (s) => s >= 80 ? "text-emerald-700" : s >= 60 ? "text-amber-700" : "text-[#D0021B]";

  return (
    <div className="min-h-screen bg-zinc-50 bg-grid">
      {/* Hero */}
      <header className="border-b border-zinc-950 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <a href="/" className="flex items-baseline gap-3">
            <span className="font-mono text-[10px] tracking-[0.18em] text-zinc-400">SMARTERP</span>
            <span className="font-display text-2xl font-black tracking-tight">Enterprise.</span>
          </a>
          <a href="/login" className="text-xs font-mono text-zinc-700 hover:text-[#FF3E00] transition-colors">SIGN IN →</a>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 pt-6 -mb-4">
        <a href="/leaderboard" className="inline-flex items-center gap-2 text-xs font-mono text-zinc-600 hover:text-[#FF3E00] transition-colors" data-testid="cf-leaderboard-link">
          / SEE THE LEADERBOARD →
        </a>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12">
        {/* Left: pitch */}
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-[#FF3E00] mb-2">/ FREE_ASSESSMENT</div>
          <h1 className="font-display text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
            Get a CFO-grade<br/>
            <span className="text-[#FF3E00]">cash flow check.</span>
          </h1>
          <p className="text-base text-zinc-600 mt-5 leading-relaxed max-w-md">
            Paste 7 numbers. Get an instant AI-generated assessment with health score, runway, and 3 prioritized actions —
            written by the same engine that powers SmartERP&apos;s analytics module.
          </p>

          <div className="mt-8 space-y-2">
            {[
              "Free · No signup required",
              "30-second result with personalized recommendations",
              "Powered by Claude Sonnet 4.5 + senior CFO frameworks",
              "We never share your numbers",
            ].map((row, i) => (
              <div key={row} className="flex items-center gap-3 text-sm">
                <span className="font-mono text-[10px] text-[#FF3E00] w-5">{String(i+1).padStart(2,"0")}</span>
                <span className="text-zinc-700">{row}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 pt-6 border-t border-zinc-300">
            <div className="font-mono text-[9px] tracking-[0.2em] text-zinc-400 mb-2">BUILT FOR</div>
            <div className="text-xs text-zinc-600">Founders, controllers, COOs, and finance leaders running businesses between $10K–$500K monthly revenue.</div>
          </div>
        </div>

        {/* Right: form */}
        <div className="border border-zinc-950 bg-white">
          <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center gap-2">
            <Sparkle size={12} weight="fill" />
            health_check.input
          </div>
          <form onSubmit={submit} className="p-6 space-y-4" data-testid="cash-health-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="label-sm text-zinc-500">Your email *</label>
                <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="health-email-input" placeholder="you@company.com" className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none transition-colors" />
              </div>
              <div className="col-span-2">
                <label className="label-sm text-zinc-500">Company (optional)</label>
                <input value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="health-company-input" placeholder="Acme Inc" className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none" />
              </div>
              {[
                { k: "monthly_revenue", l: "Monthly Revenue ($)" },
                { k: "monthly_expenses", l: "Monthly Expenses ($)" },
                { k: "cash_on_hand", l: "Cash on Hand ($)" },
                { k: "accounts_receivable", l: "Accounts Receivable ($)" },
                { k: "accounts_payable", l: "Accounts Payable ($)" },
                { k: "headcount", l: "Headcount" },
              ].map(({ k, l }) => (
                <div key={k}>
                  <label className="label-sm text-zinc-500">{l}</label>
                  <input type="number" min="0" required value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`health-${k}-input`} className="w-full border-b border-zinc-300 focus:border-zinc-950 bg-transparent py-2 text-sm outline-none tnum" />
                </div>
              ))}
            </div>

            {error && (
              <div className="border border-[#D0021B] bg-red-50 text-[#D0021B] text-xs px-3 py-2 font-mono" data-testid="health-error">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} data-testid="health-submit-btn" className="w-full bg-zinc-950 text-white font-semibold text-sm py-3 hover:bg-[#FF3E00] disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
              {loading ? "ANALYZING…" : "GET MY HEALTH CHECK"}
              {!loading && <ArrowRight size={14} weight="bold" />}
            </button>
            <div className="font-mono text-[9px] tracking-[0.15em] text-zinc-400 text-center">
              CLAUDE SONNET 4.5 · ~30 SECONDS · FREE
            </div>
          </form>
        </div>
      </div>

      {/* Results */}
      {result && (
        <div id="report-section" className="max-w-4xl mx-auto px-6 py-12 animate-fade-up">
          <div className="border border-zinc-950 bg-white">
            <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center justify-between">
              <span><ChartLineUp size={12} className="inline mr-2" /> health_check.report</span>
              <span className="text-[10px]">{form.company || form.email}</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 border-b border-zinc-950">
              <div className="border-r border-zinc-200 p-5 text-center">
                <div className="label-sm text-zinc-500">Health Score</div>
                <div className={`font-display font-black text-5xl tnum mt-1 ${scoreColor(result.health_score)}`} data-testid="health-score">
                  {result.health_score}
                </div>
                <div className="text-[10px] font-mono text-zinc-400 mt-1">/ 100</div>
              </div>
              <div className="border-r border-zinc-200 p-5 text-center">
                <div className="label-sm text-zinc-500">Net Monthly</div>
                <div className={`font-display font-black text-2xl tnum mt-1 ${result.metrics.net_monthly >= 0 ? "text-emerald-700" : "text-[#D0021B]"}`}>
                  {fmt$(result.metrics.net_monthly)}
                </div>
              </div>
              <div className="border-r border-zinc-200 p-5 text-center">
                <div className="label-sm text-zinc-500">Runway</div>
                <div className="font-display font-black text-2xl tnum mt-1">
                  {result.metrics.runway_months >= 999 ? "∞" : `${result.metrics.runway_months}mo`}
                </div>
              </div>
              <div className="p-5 text-center">
                <div className="label-sm text-zinc-500">AR / AP Ratio</div>
                <div className="font-display font-black text-2xl tnum mt-1">
                  {result.metrics.ar_ap_ratio ?? "—"}
                </div>
              </div>
            </div>

            <div className="p-6 text-sm leading-relaxed whitespace-pre-wrap text-zinc-950" data-testid="health-report-text">
              {result.report}
            </div>

            {result.share_url && (
              <ShareButtons
                shareUrl={result.share_url}
                score={result.health_score}
                company={form.company}
              />
            )}

            {/* 7-day cash forecast teaser → conversion path to free trial */}
            {result.share_id && (
              <CashForecastTeaser shareId={result.share_id} horizonDays={90} teaserDays={7} />
            )}

            <div className="border-t border-zinc-950 bg-zinc-50 px-6 py-5 flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-zinc-600">
                Want this every day, across all your business data?
              </div>
              <a href="/register" className="bg-[#FF3E00] text-white font-semibold text-xs px-5 py-2.5 hover:bg-zinc-950 transition-colors flex items-center gap-2">
                START FREE TRIAL <ArrowRight size={12} weight="bold" />
              </a>
            </div>
          </div>
        </div>
      )}

      <footer className="border-t border-zinc-200 mt-16">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between text-xs text-zinc-500">
          <span className="font-mono">SMARTERP / V1.0 · CASH FLOW HEALTH CHECK</span>
          <a href="/login" className="hover:text-zinc-950 transition-colors">Sign in →</a>
        </div>
      </footer>
    </div>
  );
}
